import { z } from 'zod'

import { generateRandomString } from '@mntn-dev/utilities'
import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  DescriptionSchema,
  NameSchema,
  TimestampSchema,
  UrlSchema,
} from './property.models.ts'
import {
  OrganizationId,
  OrganizationIdSchema,
  OrganizationUrn,
  OrganizationUrnSchema,
} from './unique-id.models.ts'

export const InternalOrganizationId = OrganizationId.Empty
export const InternalOrganizationUrn = OrganizationUrn(InternalOrganizationId)

/**
 * OrganizationType
 */
export const CustomerOrganizationTypes = ['brand', 'agency'] as const
export const InternalOrganizationTypes = ['internal'] as const

export const [OrganizationTypes, OrganizationTypeSchema, OrganizationTypeEnum] =
  EnumBuilder(...CustomerOrganizationTypes, ...InternalOrganizationTypes)
export type OrganizationType = ZodInfer<typeof OrganizationTypeSchema>

export const [, CustomerOrganizationTypeSchema, CustomerOrganizationTypeEnum] =
  EnumBuilder(...CustomerOrganizationTypes)
export type CustomerOrganizationType = ZodInfer<
  typeof CustomerOrganizationTypeSchema
>

export function isCustomerOrganizationType(
  value: string
): value is CustomerOrganizationType {
  return CustomerOrganizationTypeSchema.safeParse(value).success
}

export function isOrganizationType(value: string): value is OrganizationType {
  return OrganizationTypeSchema.safeParse(value).success
}

type OrganizationTypeLike = {
  organizationType: OrganizationType
}

export const hasOrganizationType = <
  Model extends OrganizationTypeLike,
  OrgType extends OrganizationType,
>(
  organization: Model,
  organizationType: OrgType
): organization is Model & { organizationType: OrgType } =>
  organization.organizationType === organizationType

export const OrganizationPayeeIdSchema = z.string().brand('payee')
export type OrganizationPayeeId = ZodInfer<typeof OrganizationPayeeIdSchema>

export const OrganizationPayeeId = (
  value: string = generateRandomString(16)
) => {
  return OrganizationPayeeIdSchema.parse(value)
}

/**
 * OnboardingStatus
 */
export const [
  OnboardingStatuses,
  OnboardingStatusSchema,
  OnboardingStatusEnum,
] = EnumBuilder('onboarding', 'onboarded', 'interview-failed')

export type OnboardingStatus = ZodInfer<typeof OnboardingStatusSchema>

export const getInitialOnboardingStatus = (
  type: OrganizationType
): OnboardingStatus => (type === 'agency' ? 'onboarding' : 'onboarded')

/**
 * OnboardingStepStatus
 */
export const [
  OnboardingStepStatuses,
  OnboardingStepStatusSchema,
  OnboardingStepStatusEnum,
] = EnumBuilder('completed', 'pending', 'failed')

export type OnboardingStepStatus = ZodInfer<typeof OnboardingStepStatusSchema>

/**
 * OnboardingIntermediateStepStatus
 */
export const [
  OnboardingIntermediateStepStatuses,
  OnboardingIntermediateStepStatusSchema,
  OnboardingIntermediateStepStatusEnum,
] = EnumBuilder('accepted', 'not-accepted')

export type OnboardingIntermediateStepStatus = ZodInfer<
  typeof OnboardingIntermediateStepStatusSchema
>

/**
 * OnboardingStep
 */
export const [OnboardingSteps, OnboardingStepSchema, OnboardingStepEnum] =
  EnumBuilder('profile-info', 'payee-info', 'certification', 'interview')

export type OnboardingStep = ZodInfer<typeof OnboardingStepSchema>

export const [
  InterviewCategories,
  InterviewCategorySchema,
  InterviewCategoryEnum,
] = EnumBuilder(
  'interest',
  'professionalism',
  'experience',
  'quality',
  'evaluation'
)

export type InterviewCategory = ZodInfer<typeof InterviewCategorySchema>

export const InterviewResultScoreSchema = z.int().min(0).max(4)

export type InterviewResultScore = ZodInfer<typeof InterviewResultScoreSchema>

export const InterviewResultSchema = z.record(
  InterviewCategorySchema,
  InterviewResultScoreSchema
)

export type InterviewResult = ZodInfer<typeof InterviewResultSchema>

/** Max points per category (matches InterviewResultScoreSchema max). */
export const MAX_POINTS_PER_CATEGORY = 4

/** Max total interview score (all categories × max per category). */
export const MAX_POINTS_PER_INTERVIEW =
  InterviewCategories.length * MAX_POINTS_PER_CATEGORY

/** Min score percent per category to pass; undefined = no minimum for that category. */
export const MIN_ACCEPTED_CATEGORY_SCORE_PERCENT: Partial<
  Record<InterviewCategory, number>
> = { professionalism: 50 }

/** Min total score percent (of MAX_POINTS_PER_INTERVIEW) to pass the interview. */
export const MIN_ACCEPTED_TOTAL_SCORE_PERCENT = 65

export const OrganizationOnboardingDomainSelectModelSchema = z.object({
  status: OnboardingStatusSchema,
  stepsCompleted: z.array(OnboardingStepSchema),
  interviewResult: InterviewResultSchema.optional(),
})

export type OrganizationOnboardingDomainSelectModel = ZodInfer<
  typeof OrganizationOnboardingDomainSelectModelSchema
>

export const OrganizationOnboardingDeltaModelSchema = z.object({
  status: OnboardingStatusSchema.optional(),
  stepsCompleted: z.object({
    more: z.array(OnboardingStepSchema),
    less: z.array(OnboardingStepSchema),
    same: z.array(OnboardingStepSchema),
  }),
})

export type OrganizationOnboardingDeltaModel = ZodInfer<
  typeof OrganizationOnboardingDeltaModelSchema
>

export const OrganizationDomainSelectModelSchema = z.object({
  organizationId: OrganizationIdSchema,
  organizationUrn: OrganizationUrnSchema,
  organizationType: OrganizationTypeSchema,
  name: NameSchema,
  description: DescriptionSchema.optional(),
  websiteUrl: UrlSchema.optional(),
  payeeId: OrganizationPayeeIdSchema.optional(),
  isPayable: z.boolean(),
  payeeSyncTimestamp: TimestampSchema.optional(),
  onboarding: OrganizationOnboardingDomainSelectModelSchema,
  isActive: z.boolean(),
  isArchived: z.boolean(),
  isBlocked: z.boolean(),
  isFlaggedForRemoval: z.boolean(),
  maxActiveProjectsCap: z.number().int().min(1).optional(),
})

export type OrganizationDomainSelectModel = ZodInfer<
  typeof OrganizationDomainSelectModelSchema
>

export const OrganizationDomainInsertModelSchema =
  OrganizationDomainSelectModelSchema.pick({
    organizationId: true,
    organizationType: true,
    name: true,
    description: true,
    websiteUrl: true,
  }).partial({
    organizationId: true,
  })

export type OrganizationDomainInsertModel = ZodInfer<
  typeof OrganizationDomainInsertModelSchema
>

export const OrganizationDomainUpdateModelSchema =
  OrganizationDomainSelectModelSchema.pick({
    name: true,
    description: true,
    websiteUrl: true,
    payeeId: true,
    isPayable: true,
    payeeSyncTimestamp: true,
    isArchived: true,
    isBlocked: true,
    isFlaggedForRemoval: true,
    maxActiveProjectsCap: true,
  })
    .extend({
      description: OrganizationDomainSelectModelSchema.shape.description,
      maxActiveProjectsCap: z
        .union([z.number().int().min(1), z.null()])
        // todo QFMP-5190: make non-optional when feature flag removed
        .optional(),
      onboarding: OrganizationDomainSelectModelSchema.shape.onboarding
        .extend({
          interviewResult:
            OrganizationOnboardingDomainSelectModelSchema.shape.interviewResult.nullable(),
        })
        .partial(),
    })
    .partial()

export type OrganizationDomainUpdateModel = ZodInfer<
  typeof OrganizationDomainUpdateModelSchema
>

export const OrganizationDomainSelectModelFiltersSchema = z
  .object({
    organizationId: ModelFilterSchema(OrganizationIdSchema),
    // When filtering, transform names to lower case so the
    // "lower(organizations.name)" index can be used.
    name: ModelFilterSchema(NameSchema.toLowerCase()),
    organizationType: ModelFilterSchema(OrganizationTypeSchema),
    onboardingStatus: ModelFilterSchema(OnboardingStatusSchema),
    payeeId: ModelFilterSchema(OrganizationPayeeIdSchema),
    isActive: z.boolean(),
    isBlocked: z.boolean(),
    isArchived: z.boolean(),
  })
  .partial()

export type OrganizationDomainSelectModelFilters = ZodInfer<
  typeof OrganizationDomainSelectModelFiltersSchema
>
